import { Checkbox, Drawer } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { SearchIconStroke } from '../../../../../../../assets/icons/common/common-icons';
import { CommonBadge } from '../../../../../../../common/components/CommonBadge/CommonBadge';
import { ItemLogo } from '../../../../../../../common/components/ItemLogo/ItemLogo';
import { createAssetSearch } from '../../../../../../../common/utils/fuzzy-search.utils';
import { useStore } from '../../../../../../app/data/root.store';
import { ItemMoreInfos, MoreInfosItem } from '../../../../../../wealth/pages/class/components/ClassPage.styles';
import { getDocTypeIcon } from '../../../../../assets/docsTypes/docTypes';
import { CheckboxWrapper, DocumentIcon, DocumentInfo, DocumentName, DocumentSection, DrawerBody, DrawerCloseButton, DrawerHeader, DrawerTitle, DrawerWrapper, EmptyState, ItemContent, ItemDetails, ItemName, ItemNameText, ItemRow, ItemsList, ItemsSection, SaveButton, SaveButtonContainer, SearchDescription, SearchIcon, SearchInput, SearchInputWrapper, SearchSection, SectionTitle } from './LinkedItemDrawer.styles';
import { getAllLinkableItems } from '../../../../../utils/linkable-items.utils';
export const LinkedItemDrawer = observer(({
    isOpen,
    onClose,
    fileName,
    linkedItems = [],
    onSave
}) => {
    const { metadataStore } = useStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());
    const [initiallySelectedIds, setInitiallySelectedIds] = useState(new Set());

    useEffect(() => {
        if (isOpen) {
            const newSelectedIds = new Set(linkedItems.map(item => item.id));
            setSelectedAssetIds(newSelectedIds);
            setInitiallySelectedIds(newSelectedIds);
            setSearchTerm('');

            if (!metadataStore.isLoaded && !metadataStore.isLoading) {
                metadataStore.fetchData();
            }
        }
    }, [isOpen, metadataStore, linkedItems]);

    const allAssets = useMemo(() => {
        return getAllLinkableItems(metadataStore, initiallySelectedIds);
    }, [metadataStore, initiallySelectedIds]);

    const filteredAssets = useMemo(() => {
        if (!allAssets.length) return [];

        const fuzzySearch = createAssetSearch(allAssets);
        const searchResults = fuzzySearch(searchTerm);

        return searchResults.sort((a, b) => {
            if (a.wasSelected && !b.wasSelected) return -1;
            if (!a.wasSelected && b.wasSelected) return 1;
            return 0;
        });
    }, [allAssets, searchTerm]);

    const handleItemToggle = useCallback((assetId) => {
        setSelectedAssetIds(prev => {
            const newSelectedIds = new Set(prev);
            if (newSelectedIds.has(assetId)) {
                newSelectedIds.delete(assetId);
            } else {
                newSelectedIds.add(assetId);
            }
            return newSelectedIds;
        });
    }, []);

    const handleSave = () => {
        const selectedAssets = allAssets.filter(asset => selectedAssetIds.has(asset.id));
        onSave(selectedAssets);
        onClose();
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return getDocTypeIcon();
        const extension = fileName.split('.').pop().toLowerCase();
        return getDocTypeIcon(extension);
    };

    const renderAssetItem = (asset) => {
        const isSelected = selectedAssetIds.has(asset.id);
        const displayName = asset.title || asset.name || 'Unnamed Asset';

        return (
            <ItemRow
                key={asset.id}
                onClick={() => handleItemToggle(asset.id)}
            >
                <ItemContent>
                    <ItemLogo item={asset} customStyles={{ marginRight: '0px' }} />
                    <ItemDetails>
                        <ItemName
                            // onClick={(event) => handleItemNavigation(asset, event)}
                            style={{ cursor: 'pointer' }}
                        >
                            <ItemNameText>{displayName}</ItemNameText>
                            {asset.isClosed && <CommonBadge badgeType="closed" noMargin />}
                            {asset.connectedProvider === 'vip' && <CommonBadge badgeType="vipService" noMargin />}
                            {asset.ownershipPercentage && (
                                <CommonBadge
                                    title={`${asset.ownershipPercentage}%`}
                                    badgeWrapperStyle={{
                                        background: '#DDE3EB',
                                        color: '#585D66',
                                        borderRadius: '2px 0px 0px 2px',
                                        padding: '1px 1px 0px 4px',
                                        height: '16px'
                                    }}
                                    noMargin
                                />
                            )}
                        </ItemName>
                        <ItemMoreInfos>
                            <MoreInfosItem>
                                {asset.category?.title || 'Unknown'}
                            </MoreInfosItem>
                        </ItemMoreInfos>
                    </ItemDetails>
                </ItemContent>
                <CheckboxWrapper data-checkbox-wrapper>
                    <Checkbox
                        checked={isSelected}
                        onChange={() => handleItemToggle(asset.id)}
                        onClick={(event) => event.stopPropagation()}
                    />
                </CheckboxWrapper>
            </ItemRow>
        );
    };

    return (
        <Drawer
            closable={false}
            visible={isOpen}
            width={'100%'}
            height={'auto'}
            className="linked-item-drawer-mobile"
            placement="bottom"
            destroyOnClose={true}
            onClose={onClose}
        >
            <DrawerWrapper>
                <DrawerHeader>
                    <DrawerTitle>Linked item</DrawerTitle>
                    <DrawerCloseButton onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#9097A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </DrawerCloseButton>
                </DrawerHeader>

                <DrawerBody>
                    <DocumentSection>
                        <DocumentInfo>
                            <DocumentIcon>
                                {getFileIcon(fileName)}
                            </DocumentIcon>
                            <DocumentName>{fileName}</DocumentName>
                        </DocumentInfo>
                        {selectedAssetIds.size > 0 && (
                            <div style={{
                                background: '#1A1B1D',
                                color: '#FFFFFF',
                                borderRadius: '12px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}>
                                {selectedAssetIds.size} link{selectedAssetIds.size !== 1 ? 's' : ''}
                            </div>
                        )}
                    </DocumentSection>

                    <SearchSection>
                        <SearchDescription>
                            Attach this file to portfolio items (assets or liabilities) to display it on their pages.
                        </SearchDescription>
                        <SearchInputWrapper>
                            <SearchInput
                                type="text"
                                placeholder="Search asset / liability"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <SearchIcon><SearchIconStroke /></SearchIcon>
                        </SearchInputWrapper>
                    </SearchSection>

                    <ItemsSection>
                        <SectionTitle>All Items</SectionTitle>

                        {filteredAssets.length === 0 ? (
                            <EmptyState>
                                {searchTerm ? 'No assets found matching your search.' : 'No assets available.'}
                            </EmptyState>
                        ) : (
                            <ItemsList>
                                {filteredAssets.map(renderAssetItem)}
                            </ItemsList>
                        )}
                    </ItemsSection>
                </DrawerBody>

                <SaveButtonContainer>
                    <SaveButton onClick={handleSave}>
                        Save {selectedAssetIds.size} link{selectedAssetIds.size !== 1 ? 's' : ''}
                    </SaveButton>
                </SaveButtonContainer>
            </DrawerWrapper>
        </Drawer>
    );
}); 